import { IconCloseCircle } from 'assets/img';
import { useDialog } from 'ogp/providers/dialog-provider';
import { ContextMenuItem } from 'redesign';
import { shiftAlerts, useAlert } from 'shared/components/alerts';
import {
  ShiftClaimant,
  ShiftDetailData,
} from 'shared/components/shift-detail-modal/components/tabs/claimants/claimant-table.types';
import { useInvalidateQuery } from 'shared/hooks/query-hooks/useInvalidateQuery';
import { useSiteDetection } from 'shared/hooks';
import { QUERY_KEYS } from 'api/query-keys';
import {
  getOgpCancelationDialog,
  useGetCancelationDialog,
} from '../../../shared/cancelation-dialog/cancelation-dialog';
import { isWithinCancelationPeriod } from '../../../shared/cancelation-dialog/cancelation-dialog.utils';
import { useCancelShiftClaim } from '../../../shared/cancelation-dialog/use-cancel-shift-claim';

export const OGPCancelShiftClaimAction = ({
  candidate,
  shiftData,
  forceReasonDialog = false,
}: {
  candidate: ShiftClaimant;
  shiftData: ShiftDetailData;
  forceReasonDialog?: boolean;
}) => {
  const { alertSuccess, alertError } = useAlert();
  const { openDialog, closeDialog, enableDialog, disableDialog } = useDialog();
  const { site } = useSiteDetection();
  const invalidateQuery = useInvalidateQuery();
  const cancelShiftClaim = useCancelShiftClaim(site);
  const { handleCancelShiftClaim: handleCancelWithReason } = useGetCancelationDialog();

  const handleCancelShiftClaim = (shiftData: ShiftDetailData, candidate: ShiftClaimant) => {
    const isShiftWithinCancelationPeriod = isWithinCancelationPeriod(shiftData, candidate);

    if (!forceReasonDialog && isShiftWithinCancelationPeriod) {
      const dialogContent = getOgpCancelationDialog(true);
      openDialog(dialogContent, () => {
        disableDialog();
        cancelShiftClaim.mutate(
          {
            shiftClaimId: candidate.claimId,
            cancelationReason: null,
          },
          {
            onSuccess: () => {
              invalidateQuery(QUERY_KEYS.shifts());
              invalidateQuery(QUERY_KEYS.shift(shiftData.id));
              closeDialog();
              alertSuccess(shiftAlerts.success.unsubscribed);
            },
            onError: (e) => alertError(e),
            onSettled: () => enableDialog(),
          }
        );
      });
    } else {
      handleCancelWithReason(candidate.claimId);
    }
  };

  return (
    <ContextMenuItem
      onClick={() => handleCancelShiftClaim(shiftData, candidate)}
      key={`cancel-claim-${candidate.claimId}`}
    >
      <IconCloseCircle />
      Annuleren
    </ContextMenuItem>
  );
};

export const OGPCancelAsClientAction = ({
  candidate,
  shiftData,
}: {
  candidate: ShiftClaimant;
  shiftData: ShiftDetailData;
}) => {
  const { alertSuccess, alertError } = useAlert();
  const { openDialog, closeDialog, enableDialog, disableDialog } = useDialog();
  const { site } = useSiteDetection();
  const invalidateQuery = useInvalidateQuery();
  const cancelShiftClaim = useCancelShiftClaim(site);

  const handleCancel = () => {
    const isShiftWithinCancelationPeriod = isWithinCancelationPeriod(shiftData, candidate);
    openDialog(getOgpCancelationDialog(isShiftWithinCancelationPeriod), () => {
      disableDialog();
      cancelShiftClaim.mutate(
        {
          shiftClaimId: candidate.claimId,
          cancelationReason: null,
        },
        {
          onSuccess: () => {
            invalidateQuery(QUERY_KEYS.shifts());
            invalidateQuery(QUERY_KEYS.shift(shiftData.id));
            closeDialog();
            alertSuccess(shiftAlerts.success.unsubscribed);
          },
          onError: (e) => alertError(e),
          onSettled: () => enableDialog(),
        }
      );
    });
  };

  return (
    <ContextMenuItem onClick={handleCancel} key={`cancel-claim-as-client-${candidate.claimId}`}>
      <IconCloseCircle />
      Annuleren als opdrachtgever
    </ContextMenuItem>
  );
};
